import { createApi } from '@reduxjs/toolkit/query/react';
import { subWeeks } from 'date-fns';

import { AdminApi, EmployeesApi, UsersApi } from '.';
import { setShiftStatusData } from '../actions';
import { getData, postData, putData } from '../../services/API';
import {
  addDelegateToUserUrl,
  addEmployeeToUserUrl,
  addFavoriteUnitsUrl,
  employeeConfigUrl,
  employeesToRoundUrl,
  patientsToRoundUrl,
  postEmployeeListConfigUrl,
  profilePicUrl,
  removeDelegateFromUserUrl,
  removeEmployeeFromUserUrl,
  removeFavoriteUnitsUrl,
  supportSubjectsToRoundUrl,
  updateCurrentFacilityUrl,
  updateRoundSubjectHiddenStatusUrl,
  userAccessUrl,
  userAuthUrl,
  userRoundHistoryUrl
} from '../../services/API/endpoints';

import { date } from '../../utils/helpers';

import {
  buildSubject,
  cleanEmployeeToRound,
  cleanPatientToRound
} from './utils/cleaners';
import { processError, processResponse } from './utils/transformations';

import { Employee, RoundHistory } from '../../config/interfaces';
import supportRoundingTypes from '../../constants/supportRoundingTypes.json';

interface IGetEmployeesToRoundPayload {
  hcaid: string;
  directReportsOnly?: boolean;
}

interface IGetSupportSubjectsToRoundPayload {
  facilityId: string;
  hcaid: string;
  type:
    | 'csc'
    | 'cned'
    | 'validation'
    | 'aidetValidation'
    | 'bssrValidation'
    | 'safetyValidation';
}

interface IGetRoundHistoryPayload {
  facilityId: string;
  startDate: string;
  endDate: string;
  roundingType: string;
}

// TODO: For later use
// interface IUpdateFacPayload {
//   userId: string;
//   facilityId: String;
// }
// interface IUpdateUnitPayload extends IUpdateFacPayload {
//   unitId: string;
// }
// interface IUserPayload {
//   userId: string;
// }
// interface IUserEmployeePayload extends IUserPayload {
//   employeeId: string;
// }
// interface IUserDelegatePayload {
//   delegateId: string;
// }
// interface IUserDelegatesPayload extends IUserPayload {
//   delegateIds: string[];
// }

interface IUpdateHiddenStatusPayload {
  facilityId: string;
  employeeId: string;
  roundingType: string;
  userId: string;
  visible: boolean;
}
interface IGetPatientsToRoundPayload {
  facilityId: string;
  accountNum: string;
  unitId?: string;
}

/**
 * RTK Query API slice for user features.
 *
 * @remarks
 * Provides endpoints for:
 * - User details after authentication (`getAuthorizedUser`, etc.)
 * - User management endpoints as defined
 *
 * @example
 * import { useGetAuthorizedUserQuery } from 'src/redux/apis/Users.api';
 * const { data } = useGetAuthorizedUserQuery(userId);
 */
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'authorized-user',
    'employee-config',
    'employee-rounding-list-config',
    'employee-subject-config',
    'patient-to-round',
    'patients-to-round-facility',
    'employees-to-round',
    'support-subjects-to-round',
    'round-history',
    'users'
  ],
  endpoints: builder => ({
    getAuthorizedUser: builder.query({
      query: userId => getData(`${userAuthUrl}?userId=${userId}`, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, userId) => [
        { type: 'authorized-user', id: userId }
      ]
    }),
    postAddEmployeeToUserFavs: builder.mutation({
      query: payload => postData(addEmployeeToUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postUpdateCurrentFacility: builder.mutation({
      query: payload => postData(updateCurrentFacilityUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-subject-config' }
      ]
    }),
    postRemoveEmployeeFromUserFavs: builder.mutation({
      query: payload => postData(removeEmployeeFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postUpdateUserProfilePic: builder.mutation({
      query: ({ hcaid, pic }) => {
        const multiFormData = new FormData();
        multiFormData.append('picture', pic);
        return postData(`${profilePicUrl}?userId=${hcaid}`, multiFormData);
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      // TODO: need to reload sas token
      invalidatesTags: () => []
    }),
    postAddFavoriteUnit: builder.mutation({
      query: payload => postData(addFavoriteUnitsUrl, payload),
      transformResponse: processResponse,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Added'
      // });
      transformErrorResponse: processError,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Adding_Failed'
      // });
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    postRemoveFavoriteUnit: builder.mutation({
      query: payload => postData(removeFavoriteUnitsUrl, payload),
      transformResponse: processResponse,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Removed'
      // });
      transformErrorResponse: processError,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Removal_Failed'
      // });
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    postAddDelegateToUser: builder.mutation({
      query: payload => postData(addDelegateToUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postRemoveDelegateFromUser: builder.mutation({
      query: payload => postData(removeDelegateFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postRemoveUserAsDelegate: builder.mutation({
      query: payload => postData(removeDelegateFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.delegateId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    getEmployeeConfig: builder.query({
      keepUnusedDataFor: 0,
      query: (employeeId: string) =>
        getData(`${employeeConfigUrl}?employeeId=${employeeId}`, {}),
      transformResponse: response => {
        const directEmployees = (response?.directsList ?? []).map(
          (employee: any) => ({
            ...employee,
            hcaid: employee.userKey.split(':').pop(),
            dept: employee.department,
            managerFullName: employee.manager
          })
        );
        const formattedListsDelegatedToMe = (
          response?.roundingListsDelegatedToMe ?? []
        ).map((employee: any) => ({
          ...employee,
          employeeName: `${employee.firstName} ${employee.lastName}`
        }));
        const favEmployees = (response?.favorites ?? []).map(
          (employee: any) => ({
            ...employee,
            hcaid: employee.userKey.split(':').pop(),
            dept: employee.department,
            managerFullName: employee.manager
          })
        );
        return {
          directEmployees,
          delegatedToEmployees: response?.employeesDelegatedTo ?? [],
          delegatedToMeEmployees: formattedListsDelegatedToMe,
          favEmployees
        };
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        { type: 'employee-config', id: employeeId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    getRoundingListConfig: builder.query({
      query: (employeeId: string) =>
        getData(`${employeeConfigUrl}?employeeId=${employeeId}`, {}),
      transformResponse: response => response.myRoundingListConfig,
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        { type: 'employee-rounding-list-config', id: employeeId }
      ]
    }),
    getEmployeeSubjectConfig: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: { employeeId: string; roundingType: string }) =>
        getData(
          `${employeeConfigUrl}/${payload.roundingType}?employeeId=${payload.employeeId}`,
          {}
        ),
      transformResponse: response =>
        response.map((employee: any) => ({
          ...employee,
          hcaid: employee.userKey?.split(':').pop() ?? '',
          dept: employee.department,
          managerFullName: employee.manager,
          employeeName: `${employee.firstName} ${employee.lastName}`,
          visible: employee.visible
        })),
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-subject-config',
          id: payload.roundingType
        }
      ]
    }),
    updateSubjectHiddenStatus: builder.mutation({
      query: (payload: IUpdateHiddenStatusPayload) => {
        const { facilityId, employeeId, roundingType, visible } = payload;
        return putData(
          `${updateRoundSubjectHiddenStatusUrl}/${roundingType}?facilityId=${facilityId}&id=${employeeId}&visible=${visible}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        {
          type: 'employee-subject-config',
          id: payload.roundingType
        }
      ]
    }),
    postUpdateRoundingListConfig: builder.mutation({
      query: payload => postData(postEmployeeListConfigUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    getPatientToRound: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientsToRoundPayload) =>
        getData(
          `${patientsToRoundUrl}?facilityId=${payload.facilityId}&accountNum=${payload.accountNum}&unitId=${payload.unitId}`,
          {}
        ),
      transformResponse: response => {
        if (response.length) {
          return response[0];
        }
        return { discharged: true };
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-to-round',
          id: `${payload.facilityId}:${payload.accountNum}:${payload.unitId}`
        }
      ]
    }),
    getPatientsToRoundByFacility: builder.query({
      keepUnusedDataFor: 0,
      query: (facilityId: string) =>
        getData(`${patientsToRoundUrl}?facilityId=${facilityId}`, {}),
      transformResponse: response =>
        response.map((patient: any) => cleanPatientToRound(patient)),
      transformErrorResponse: processError,
      providesTags: (_result, _error, facilityId) => [
        {
          type: 'patients-to-round-facility',
          id: facilityId
        }
      ]
    }),
    getEmployeesToRound: builder.query<Employee[], IGetEmployeesToRoundPayload>(
      {
        keepUnusedDataFor: 0,
        query: payload =>
          getData(
            `${employeesToRoundUrl}?userId=${payload.hcaid}&directReportsOnly=${
              payload.directReportsOnly ?? false
            }`,
            {}
          ),
        transformResponse: response =>
          response.map((employee: any) => cleanEmployeeToRound(employee)),
        transformErrorResponse: processError,
        providesTags: (_result, _error, payload) => [
          {
            type: 'employees-to-round',
            id: JSON.stringify(payload)
          }
        ]
      }
    ),
    getSupportSubjectsToRound: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        payload: IGetSupportSubjectsToRoundPayload,
        { dispatch }
      ) => {
        try {
          const supportSubjectsResponse: any = await getData(
            `${supportSubjectsToRoundUrl}?userId=${payload.hcaid}&supportType=${
              payload.type === 'csc' ? 'csrn' : payload.type
            }`,
            {}
          );

          if (supportSubjectsResponse.error)
            throw new Error(supportSubjectsResponse.error);

          const cleanedSubjectsToRound: Employee[] =
            supportSubjectsResponse.data.map((employee: any) => ({
              ...cleanEmployeeToRound(employee),
              ...(payload.type === 'csc' && employee.rnExperienceDate
                ? {
                    rnExperienceDate: date.parseMonthDayYearToYearMonthDay(
                      employee.rnExperienceDate
                    )
                  }
                : {})
            }));

          const subjectsToRoundIds = new Set(
            cleanedSubjectsToRound.map(subject => subject.hcaid)
          );

          const now = new Date();
          const today = date.convertToDayOfYear(now);
          // TODO: should be a switch case for diff support types on how long to retrieve adhocs
          const twoWeeksAgo = date.convertToDayOfYear(subWeeks(now, 2));

          const userRoundHistory = await dispatch(
            UsersApi.endpoints.getRoundHistory.initiate({
              facilityId: payload.facilityId,
              roundingType: payload.type === 'csc' ? 'csrn' : payload.type,
              startDate: date.formatFilterStartDate(twoWeeksAgo),
              endDate: date.formatFilterEndDate(today)
            })
          ).unwrap();

          const adHocRounds = userRoundHistory.reduce(
            (acc: RoundHistory[], history: any) => {
              if (
                !subjectsToRoundIds.has(history.employeeId) &&
                (supportRoundingTypes.validation.includes(payload.type)
                  ? history?.tags?.length > 0
                  : true)
              ) {
                const subject = buildSubject(history);

                const duplicateIndex = acc.findIndex(
                  tempSubject => tempSubject.hcaid === subject.hcaid
                );

                if (duplicateIndex !== -1) {
                  if (
                    history?.roundDate &&
                    history?.roundDate > acc[duplicateIndex].roundDate
                  ) {
                    acc[duplicateIndex] = subject;
                  } else {
                    return acc;
                  }
                }

                return [...acc, subject];
              }
              return acc;
            },
            []
          );

          const combinedSubjectsAndAdHocRounds: (Employee & RoundHistory)[] = [
            ...cleanedSubjectsToRound,
            ...adHocRounds
          ];

          let subjectsToRound: (Employee &
            RoundHistory & {
              onShift?: boolean;
            })[] = combinedSubjectsAndAdHocRounds;

          if (payload.type === 'csc') {
            // add onShift status for CSC subjects
            const hcaIds = combinedSubjectsAndAdHocRounds.map(emp => emp.hcaid);

            const shiftStatus = await dispatch(
              EmployeesApi.endpoints.postShiftStatusListByIds.initiate(hcaIds)
            ).unwrap();
            // this action trigger is temporary to identify if filter button on csc list needs to show or not
            if (shiftStatus.length > 0) dispatch(setShiftStatusData(true));

            subjectsToRound = subjectsToRound.map(emp => {
              const shiftInfo = shiftStatus.find(
                (shift: any) => shift.userId === emp.hcaid
              );
              return {
                ...emp,
                onShift: shiftInfo ? shiftInfo.onShift : false
              } as Employee & RoundHistory & { onShift?: boolean };
            });
          }

          return {
            data: subjectsToRound
          };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, payload) => [
        {
          type: 'support-subjects-to-round',
          id: JSON.stringify(payload)
        }
      ]
    }),
    getRoundHistory: builder.query({
      query: (payload: IGetRoundHistoryPayload) => {
        const { roundingType, facilityId, startDate, endDate } = payload;
        return getData(
          `${userRoundHistoryUrl}?roundingType=${roundingType}&facilityId=${facilityId}&startDate=${startDate}&endDate=${endDate}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'round-history',
          id: JSON.stringify(payload)
        }
      ]
    }),
    postUserAccess: builder.mutation({
      query: payload => postData(userAccessUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(AdminApi.util.invalidateTags([{ type: 'users', id: 'LIST' }]));
      }
    })
  })
});

export const {
  useGetAuthorizedUserQuery,
  useLazyGetAuthorizedUserQuery,
  usePostAddEmployeeToUserFavsMutation,
  usePostUpdateCurrentFacilityMutation,
  usePostRemoveEmployeeFromUserFavsMutation,
  usePostUpdateUserProfilePicMutation,
  usePostAddFavoriteUnitMutation,
  usePostRemoveFavoriteUnitMutation,
  usePostAddDelegateToUserMutation,
  usePostRemoveDelegateFromUserMutation,
  usePostRemoveUserAsDelegateMutation,
  useGetEmployeeConfigQuery,
  useLazyGetEmployeeConfigQuery,
  useGetRoundingListConfigQuery,
  useLazyGetRoundingListConfigQuery,
  useGetEmployeeSubjectConfigQuery,
  useLazyGetEmployeeSubjectConfigQuery,
  useUpdateSubjectHiddenStatusMutation,
  usePostUpdateRoundingListConfigMutation,
  useGetPatientToRoundQuery,
  useLazyGetPatientToRoundQuery,
  useGetPatientsToRoundByFacilityQuery,
  useLazyGetPatientsToRoundByFacilityQuery,
  useGetEmployeesToRoundQuery,
  useLazyGetEmployeesToRoundQuery,
  useGetSupportSubjectsToRoundQuery,
  useLazyGetSupportSubjectsToRoundQuery,
  useGetRoundHistoryQuery,
  useLazyGetRoundHistoryQuery,
  usePostUserAccessMutation
} = usersApi;
